export const cleanupTransactionPrompt = {
  contextPrefix: `
The following is a single transaction the user explicitly selected for cleanup, along with the full list of
categories and tags available in their account. Use ONLY this data - do not look up or invent information about
the merchant, category, or tags.
`,
  prompt: `
You are reviewing a single transaction and proposing cleanup suggestions across its editable fields.

The only fields you may propose changes for are: "payee", "category", "memo", "tags", "user_flag". Never propose
changes to amount, date, status, or account - those are factual bank data, not stylistic.

Rules:
- For each field, only include a suggestion if you have a confident, meaningfully better value than the current one.
  If a field already looks clean and correct, omit it entirely - do not force a change.
- It is fine to return an empty suggestions array if the transaction is already clean across every field.
- "payee": actively compare the current payee against how a well-formatted merchant name should look (proper
  casing, no raw transaction codes/numbers/store IDs/locations bolted on, no ALL CAPS). Having a payee already
  filled in is not a reason to skip this field - propose a polished variant whenever one is cleaner than what's
  there now, even if the current value is legible. Keep it clearly tied to the current merchant, just
  normalized/de-cluttered. Never guess a different merchant.
- "category": propose a category ONLY from the provided allowed categories list. Return both "proposedValue" (the
  category name) and "proposedCategoryId" (its exact id from the list).
- "memo": propose a short, clean memo when the current one is missing, noisy, or unhelpful.
- "tags": propose a full replacement list of tag names ONLY drawn from the provided allowed tags list, returned in
  "proposedTags". Only propose tags when they are clearly and confidently relevant to this transaction.
- "user_flag": propose one of RED, ORANGE, YELLOW, GREEN, BLUE, VIOLET, BLACK, BROWN, NONE in "proposedValue".

Return a JSON object with a single "suggestions" key: an array of objects, each with:
- "field": one of "payee", "category", "memo", "tags", "user_flag"
- "proposedValue": the proposed value as a string, or null when the field is "tags" (use "proposedTags" instead)
- "proposedCategoryId": the exact id of the proposed category from the allowed list, or null unless field is "category"
- "proposedTags": the full proposed list of tag names, or null unless field is "tags"
- "confidence": a number from 0 to 1 indicating how confident you are in the suggestion
- "rationale": one short sentence explaining the suggestion
`,
  schema: {
    name: 'CleanupTransactionProposalResponse',
    schema: {
      type: 'object',
      additionalProperties: false,
      strict: true,
      properties: {
        suggestions: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              field: { type: 'string', enum: ['payee', 'category', 'memo', 'tags', 'user_flag'] },
              proposedValue: { type: ['string', 'null'] },
              proposedCategoryId: { type: ['string', 'null'] },
              proposedTags: { type: ['array', 'null'], items: { type: 'string' } },
              confidence: { type: 'number' },
              rationale: { type: 'string' },
            },
            required: ['field', 'proposedValue', 'proposedCategoryId', 'proposedTags', 'confidence', 'rationale'],
          },
        },
      },
      required: ['suggestions'],
    },
  },
};
